import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useState } from 'react';
import TemplateInput, { TemplateDisplay, TemplateOption } from '../../../components/common/TemplateModal/TemplateInput';
import { fetchEvent, useCreateEvent } from '../../../services/react-query/events';
import { queryClient } from '../../../services/react-query/queryClient';
import { queryKeys } from '../../../services/react-query/queryKeys';
import { headerTypographyProps } from '../../../styles/sxStyling';
import { Event } from '../../../types/Event';
import { Loading } from '../../../uikit';
import { parseIsoWithDefault } from '../../../utility/dateUtils';

interface AddMultiSubEventsModalProps {
  isOpen: boolean;
  onCancel: () => void;
  parentEvent: Event;
  templates: TemplateOption[];
}

interface ModalState {
  startAt: DateTime | null;
  endAt: DateTime | null;
  template: TemplateDisplay | null;
}

interface CreationState {
  isCreating: boolean;
  error: string | null;
  created: number;
  total: number;
}

type FormField = keyof ModalState;
type ValidationErrors = Partial<Record<FormField, string>>;

interface FormState {
  values: ModalState;
  touched: boolean;
}

interface FormValidationResult {
  errors: ValidationErrors;
  subEventCount: number;
}

const MAX_SUBEVENT_DAYS = 30;

const INITIAL_CREATION_STATE: CreationState = {
  isCreating: false,
  error: null,
  created: 0,
  total: 0,
};

// Creates the initial form values from the parent event's date range.
const createInitialModalState = (startAt: Event['start_at'], endAt: Event['end_at']): ModalState => ({
  startAt: parseIsoWithDefault(startAt),
  endAt: parseIsoWithDefault(endAt),
  template: null,
});

// Returns the valid selectable range for both date pickers.
const getDatePickerBounds = ({ startAt, endAt }: Pick<ModalState, 'startAt' | 'endAt'>) => ({
  minStartDate: endAt?.isValid ? endAt.minus({ days: MAX_SUBEVENT_DAYS - 1 }) : undefined,
  maxStartDate: endAt?.isValid ? endAt : undefined,
  minEndDate: startAt?.isValid ? startAt : undefined,
  maxEndDate: startAt?.isValid ? startAt.plus({ days: MAX_SUBEVENT_DAYS - 1 }) : undefined,
});

// Validates the form and calculates the number of subevents in the selected range.
const validateForm = ({ startAt, endAt, template }: ModalState): FormValidationResult => {
  const errors: ValidationErrors = {};
  let subEventCount = 0;

  if (!startAt?.isValid) {
    errors.startAt = 'Please select a valid start date.';
  }

  if (!endAt?.isValid) {
    errors.endAt = 'Please select a valid end date.';
  }

  if (!template) {
    errors.template = 'Please select an event template.';
  }

  if (startAt?.isValid && endAt?.isValid) {
    const startDay = startAt.startOf('day');
    const endDay = endAt.startOf('day');

    if (endDay.toMillis() < startDay.toMillis()) {
      errors.endAt = 'End date must be on or after the start date.';
    } else {
      subEventCount = Math.floor(endDay.diff(startDay, 'days').days) + 1;

      if (subEventCount > MAX_SUBEVENT_DAYS) {
        errors.endAt = `You can only create up to ${MAX_SUBEVENT_DAYS} subevents at once.`;
      }
    }
  }

  return {
    errors,
    subEventCount,
  };
};

// Creates the complete initial form state and exposes invalid parent dates immediately.
const createInitialFormState = (startAt: Event['start_at'], endAt: Event['end_at']): FormState => {
  const values = createInitialModalState(startAt, endAt);
  const { errors } = validateForm(values);

  return {
    values,
    touched: Boolean(errors.startAt || errors.endAt),
  };
};

// Builds one event payload for each day in the selected date range.
const buildSubEventPayloads = (templateEvent: Event, parentEvent: Event, startAt: DateTime, subEventCount: number): Event[] => {
  if (!startAt.isValid) {
    throw new Error('Unable to build subevents from an invalid start date.');
  }

  const parentEventFields = {
    ..._.pick(parentEvent, ['status', 'account_uuid', 'account_name', 'account_code', 'account_display_name']),
    parent_uuid: parentEvent.uuid,
    parent_name: parentEvent.name,
  };

  return _.times(subEventCount, (index) => {
    const subEventStartAt = startAt.plus({ days: index });
    const subEventEndAt = subEventStartAt.plus({ days: 1 });

    return {
      ..._.cloneDeep(templateEvent),
      ...parentEventFields,
      name: `${parentEvent.name} Subevent`,
      start_at: subEventStartAt.toISO()!,
      end_at: subEventEndAt.toISO()!,
      multi_child: true,
      children: [],
    };
  });
};

export default function AddMultiSubEventsModal({ isOpen, onCancel, parentEvent, templates }: AddMultiSubEventsModalProps) {
  const { uuid: parentUuid, start_at: parentStartAt, end_at: parentEndAt } = parentEvent;

  const [formState, setFormState] = useState<FormState>(() => createInitialFormState(parentStartAt, parentEndAt));

  const [creationState, setCreationState] = useState<CreationState>(INITIAL_CREATION_STATE);

  const { mutateAsync: createEvent } = useCreateEvent({});

  const { values, touched } = formState;
  const { startAt, endAt, template } = values;
  const { isCreating, error, created, total } = creationState;

  const validation = validateForm(values);
  const isFormValid = _.isEmpty(validation.errors);

  const { minStartDate, maxStartDate, minEndDate, maxEndDate } = getDatePickerBounds(values);

  // Restores the form and creation progress using the latest parent event.
  const resetState = useCallback(() => {
    setFormState(createInitialFormState(parentStartAt, parentEndAt));
    setCreationState(INITIAL_CREATION_STATE);
  }, [parentStartAt, parentEndAt]);

  // Resets the modal when it opens or the parent event dates change.
  useEffect(() => {
    if (!isOpen) return;

    resetState();
  }, [isOpen, parentUuid, resetState]);

  // Updates one field, exposes form validation, and clears any previous request error.
  const updateModalField = <K extends FormField>(field: K, value: ModalState[K]) => {
    setFormState((previousState) => ({
      values: {
        ...previousState.values,
        [field]: value,
      },
      touched: true,
    }));

    setCreationState((previousState) => (previousState.error ? { ...previousState, error: null } : previousState));
  };

  // Exposes all validation errors once the user interacts with the form.
  const markTouched = () => {
    setFormState((previousState) => {
      if (previousState.touched) {
        return previousState;
      }

      return {
        ...previousState,
        touched: true,
      };
    });
  };

  // Returns an error after the user has interacted with the form.
  const getFieldError = (field: FormField): string | undefined => {
    return touched ? validation.errors[field] : undefined;
  };

  // Returns MUI-compatible error props after the form has been touched.
  const getFieldErrorProps = (field: FormField, defaultHelperText?: string) => {
    const message = getFieldError(field);

    return {
      error: Boolean(message),
      helperText: message ?? defaultHelperText,
    };
  };

  // Closes and resets the modal unless subevent creation is currently running.
  const handleCancel = () => {
    if (isCreating) return;

    resetState();
    onCancel();
  };

  // Creates all subevents sequentially so the displayed progress stays accurate.
  const handleCreate = async () => {
    if (!isFormValid || !startAt || !template) {
      return;
    }

    const subEventCount = validation.subEventCount;

    try {
      setCreationState({
        isCreating: true,
        error: null,
        created: 0,
        total: subEventCount,
      });

      const templateEvent = await fetchEvent('new', template.uuid ?? undefined);

      const payloads = buildSubEventPayloads(templateEvent, parentEvent, startAt, subEventCount);

      for (const payload of payloads) {
        await createEvent(payload);

        setCreationState((previousState) => ({
          ...previousState,
          created: previousState.created + 1,
        }));
      }

      // Invalidate the parent event query to show the new subevents.
      queryClient.invalidateQueries({ queryKey: [queryKeys.allEvents, parentUuid] });

      resetState();
      onCancel();
    } catch {
      setCreationState((previousState) => ({
        ...previousState,
        isCreating: false,
        error: 'Something went wrong while creating subevents. Please try again.',
      }));
    }
  };

  const createButtonText = isCreating ? `Creating ${created}/${total}` : 'Create';

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <Typography {...headerTypographyProps} sx={{ m: 2 }}>
        Add Multiple Subevents
      </Typography>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <DatePicker
          label="Start Date"
          value={startAt}
          onChange={(value) => updateModalField('startAt', value)}
          minDate={minStartDate}
          maxDate={maxStartDate}
          disabled={isCreating}
          slotProps={{
            textField: {
              ...getFieldErrorProps('startAt'),
              onBlur: markTouched,
            },
          }}
        />

        <DatePicker
          label="End Date"
          value={endAt}
          onChange={(value) => updateModalField('endAt', value)}
          minDate={minEndDate}
          maxDate={maxEndDate}
          disabled={isCreating}
          slotProps={{
            textField: {
              ...getFieldErrorProps('endAt', `Select up to ${MAX_SUBEVENT_DAYS} days, including the start date.`),
              onBlur: markTouched,
            },
          }}
        />

        <TemplateInput
          templates={templates}
          value={template}
          onChange={(option) => updateModalField('template', option)}
          onBlur={markTouched}
          label="Event Template"
          disabled={isCreating}
          {...getFieldErrorProps('template')}
        />

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleCancel} disabled={isCreating}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleCreate}
          disabled={isCreating || !isFormValid}
          startIcon={isCreating ? <Loading size={16} color="inherit" /> : undefined}
        >
          {createButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
